// @ts-nocheck
/* eslint-disable */

import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, Select, MenuItem, InputLabel, FormControl, Box,
} from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useTypedSelector } from 'helpers';
import { hasWriteAccessToMasterConfigSubSystem } from 'selectors';

// v7.1: USER-level eligibility documents — same UX as the old card-level table
// (MUI table + add/edit/delete modals + validation), repointed to user_eligibility.
const BASE_URL = process.env.REACT_APP_ABT_ALL_APIS_BASE_URL;
const EL = `${BASE_URL}/api/v1/web_portal/fare_media_policy_capping/user_eligibility`;
const DT = `${BASE_URL}/api/v1/web_portal/fare_media_policy_capping/document_types`;

const emptyForm = { document_type_id: '', issued_at: '', expires_at: '', remarks: '', internal_id: '' };

const validateRemarksClient = (remarks, rules) => {
  if (!rules || !remarks) return null;
  const { input_type, min_length, max_length, allow_special_chars, special_chars } = rules;
  if (min_length && remarks.length < min_length) return `Remarks must be at least ${min_length} characters long`;
  if (max_length && remarks.length > max_length) return `Remarks must be at most ${max_length} characters long`;
  if (input_type) {
    const allowedSpecial = new Set(allow_special_chars && Array.isArray(special_chars) ? special_chars : []);
    for (const char of remarks) {
      const isDigit = /[0-9]/.test(char);
      const isAlpha = /[A-Za-z؀-ۿ]/u.test(char);
      let ok = allowedSpecial.has(char);
      if (input_type === 'numeric') ok = ok || isDigit;
      else if (input_type === 'alpha') ok = ok || isAlpha;
      else if (input_type === 'alphanumeric') ok = ok || isDigit || isAlpha;
      if (!ok) {
        const note = allowedSpecial.size > 0 ? ` and special characters: ${[...allowedSpecial].join(' ')}` : '';
        if (input_type === 'numeric') return `Identifier must contain numbers only${note}`;
        if (input_type === 'alpha') return `Identifier must contain letters only${note}`;
        return `Identifier must contain letters and numbers only${note}`;
      }
    }
    if (input_type === 'alphanumeric') {
      if (!/[A-Za-z؀-ۿ]/u.test(remarks)) return 'Identifier must contain at least one letter (requires both letters and numbers)';
      if (!/[0-9]/.test(remarks)) return 'Identifier must contain at least one number (requires both letters and numbers)';
    }
  }
  return null;
};

const generateExample = (rules) => {
  if (!rules || !rules.input_type) return null;
  const { input_type, min_length, allow_special_chars, special_chars } = rules;
  const targetLen = min_length ? Math.max(min_length, 4) : 6;
  let base = '';
  if (input_type === 'numeric') base = '1234567890'.repeat(4).slice(0, targetLen);
  else if (input_type === 'alpha') base = 'ABCDEFGHIJ'.repeat(3).slice(0, targetLen);
  else base = 'ABC12DEFGH'.repeat(3).slice(0, targetLen);
  if (allow_special_chars && special_chars?.length > 0) {
    const mid = Math.floor(base.length / 2);
    base = base.slice(0, mid) + special_chars[0] + base.slice(mid);
  }
  return base;
};

const RulesHintBox = ({ rules, lang }) => {
  if (!rules) return null;
  const isAr = lang === 'ar-eg';
  const lines = [];
  if (rules.input_type) {
    const typeMap = {
      numeric: isAr ? 'أرقام فقط' : 'Numbers only',
      alpha: isAr ? 'أحرف فقط' : 'Letters only',
      alphanumeric: isAr ? 'أحرف وأرقام (كلاهما مطلوب)' : 'Letters & numbers (both required)',
    };
    lines.push((isAr ? 'النوع: ' : 'Type: ') + (typeMap[rules.input_type] || rules.input_type));
  }
  if (rules.min_length) lines.push((isAr ? 'الحد الأدنى: ' : 'Min length: ') + rules.min_length);
  if (rules.max_length) lines.push((isAr ? 'الحد الأقصى: ' : 'Max length: ') + rules.max_length);
  if (rules.allow_special_chars && rules.special_chars?.length) {
    lines.push((isAr ? 'أحرف خاصة مسموحة: ' : 'Allowed special chars: ') + rules.special_chars.join('  '));
  }
  if (lines.length === 0) return null;
  const example = generateExample(rules);
  return (
    <Box style={{ backgroundColor: '#e8f4fd', border: '1px solid #91caff', borderRadius: 6, padding: '8px 12px', marginTop: 4 }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <InfoOutlinedIcon style={{ fontSize: 14, color: '#1890ff' }} />
        <span style={{ fontSize: 12, fontWeight: 'bold', color: '#1890ff' }}>{isAr ? 'قواعد التحقق:' : 'Identifier validation rules:'}</span>
      </Box>
      {lines.map((line, i) => (<div key={i} style={{ fontSize: 12, color: '#555', paddingLeft: 18 }}>• {line}</div>))}
      {example && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, paddingLeft: 18 }}>
          <span style={{ fontSize: 12, color: '#888' }}>{isAr ? 'مثال صحيح:' : 'Valid example:'}</span>
          <code style={{ fontSize: 13, backgroundColor: '#fff', color: '#1890ff', padding: '2px 10px', borderRadius: 4, fontFamily: 'monospace', fontWeight: 'bold', border: '1px solid #91caff' }}>{example}</code>
        </Box>
      )}
    </Box>
  );
};

const UserEligibility = ({ userId }) => {
  const intl = useIntl();
  const lang = localStorage.getItem('EASY_MOBILITY_LANGUAGE') || 'en-us';
  const hasWriteAccess = useTypedSelector((s) => hasWriteAccessToMasterConfigSubSystem(s, 'Users'));

  let userAuthData = '';
  let adminId = null;
  const authData = localStorage.getItem('authData');
  if (authData) {
    const parsed = JSON.parse(authData);
    userAuthData = parsed.userToken;
    adminId = parsed.userData?.id;
  }
  const headers = { 'access-token': userAuthData?.['access-token'], uid: userAuthData?.uid, client: userAuthData?.client };

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [remarksError, setRemarksError] = useState('');

  const selectedDocType = docTypes.find((dt) => dt.document_types_id === form.document_type_id);
  const activeRules = selectedDocType?.validation_rules || null;

  const load = () => {
    setIsLoading(true);
    axios.get(`${EL}?user_id=${userId}&pageSize=1000`, { headers })
      .then((res) => setData(res.data?.data?.userEligibility || []))
      .catch(() => setData([]))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
    axios.get(`${DT}?pageSize=1000`, { headers })
      .then((res) => setDocTypes(res.data?.data?.documentTypes || []))
      .catch(() => setDocTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setRemarksError(''); setOpenDialog(true); };
  const openEdit = (row) => {
    setEditId(row.user_eligibility_id);
    setForm({
      document_type_id: row.document_type_id || '',
      issued_at: row.issued_at ? String(row.issued_at).split('T')[0] : '',
      expires_at: row.expires_at ? String(row.expires_at).split('T')[0] : '',
      remarks: row.remarks || '',
      internal_id: row.internal_id || '',
    });
    setRemarksError('');
    setOpenDialog(true);
  };

  const handleRemarksChange = (value) => {
    setForm((p) => ({ ...p, remarks: value }));
    setRemarksError(activeRules ? (validateRemarksClient(value, activeRules) || '') : '');
  };
  const handleDocTypeChange = (docTypeId) => {
    setForm((p) => ({ ...p, document_type_id: docTypeId }));
    const dt = docTypes.find((d) => d.document_types_id === docTypeId);
    setRemarksError(validateRemarksClient(form.remarks, dt?.validation_rules || null) || '');
  };

  const handleSave = async () => {
    const clientError = validateRemarksClient(form.remarks, activeRules);
    if (clientError) { setRemarksError(clientError); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.patch(`${EL}/${editId}?lang=${lang}`, {
          document_type_id: form.document_type_id || undefined,
          issued_at: form.issued_at || undefined,
          expires_at: form.expires_at || undefined,
          verified_by: adminId,
          remarks: form.remarks || undefined,
          internal_id: form.internal_id || undefined,
        }, { headers });
        toast.success(intl.formatMessage({ id: 'EligibilityUpdated' }));
      } else {
        await axios.post(`${EL}?lang=${lang}`, {
          user_id: userId,
          document_type_id: form.document_type_id,
          issued_at: form.issued_at || undefined,
          expires_at: form.expires_at || undefined,
          verified_by: adminId,
          remarks: form.remarks || undefined,
          internal_id: form.internal_id,
        }, { headers });
        toast.success(intl.formatMessage({ id: 'EligibilityAdded' }));
      }
      setOpenDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${EL}/${id}?lang=${lang}`, { headers });
      toast.success(intl.formatMessage({ id: 'EligibilityDeleted' }));
      setDeleteConfirmId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || String(err));
    }
  };

  return (
    <div style={{ marginTop: '4px' }}>
      {hasWriteAccess && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            style={{ backgroundColor: '#363740', color: '#fff', borderRadius: '6px' }} onClick={openCreate}>
            {intl.formatMessage({ id: 'add' })}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}><CircularProgress /></div>
      ) : (
        <Table size="small" aria-label="user eligibility table">
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'internal_id' })}</TableCell>
              <TableCell align="center" style={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'DocumentType' })}</TableCell>
              <TableCell align="center" style={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'issued_at' })}</TableCell>
              <TableCell align="center" style={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'expires_at' })}</TableCell>
              <TableCell align="center" style={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'DocumentIdentifier' })}</TableCell>
              {hasWriteAccess && <TableCell align="center" style={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'actions' })}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((row, index) => (
                <TableRow key={row.user_eligibility_id || index}>
                  <TableCell align="center">{row.internal_id || '-'}</TableCell>
                  <TableCell align="center">{lang === 'ar-eg' ? (row.document_type_ar_name || row.document_type_en_name || '-') : (row.document_type_en_name || row.document_type_ar_name || '-')}</TableCell>
                  <TableCell align="center">{row.issued_at ? String(row.issued_at).split('T')[0] : '-'}</TableCell>
                  <TableCell align="center">{row.expires_at ? String(row.expires_at).split('T')[0] : '-'}</TableCell>
                  <TableCell align="center">{row.remarks || '-'}</TableCell>
                  {hasWriteAccess && (
                    <TableCell align="center">
                      <Tooltip title={intl.formatMessage({ id: 'edit' })}>
                        <IconButton size="small" onClick={() => openEdit(row)}><EditIcon style={{ fontSize: '18px', color: '#363740' }} /></IconButton>
                      </Tooltip>
                      <Tooltip title={intl.formatMessage({ id: 'delete' })}>
                        <IconButton size="small" onClick={() => setDeleteConfirmId(row.user_eligibility_id)}><DeleteIcon style={{ fontSize: '18px', color: '#e53935' }} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={hasWriteAccess ? 6 : 5} style={{ textAlign: 'center' }}>{intl.formatMessage({ id: 'NoDateAvailable' })}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? intl.formatMessage({ id: 'edit' }) : intl.formatMessage({ id: 'add' })} {intl.formatMessage({ id: 'Eligibility' })}</DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px' }}>
          <FormControl variant="outlined" size="small" fullWidth required>
            <InputLabel>{intl.formatMessage({ id: 'DocumentType' })}</InputLabel>
            <Select value={form.document_type_id} onChange={(e) => handleDocTypeChange(e.target.value)} label={intl.formatMessage({ id: 'DocumentType' })}>
              {docTypes.map((dt) => (<MenuItem key={dt.document_types_id} value={dt.document_types_id}>{lang === 'ar-eg' ? dt.ar_name : dt.en_name}</MenuItem>))}
            </Select>
          </FormControl>
          <TextField label={intl.formatMessage({ id: 'internal_id' })} value={form.internal_id} onChange={(e) => setForm({ ...form, internal_id: e.target.value })} variant="outlined" size="small" fullWidth required={!editId} InputLabelProps={{ shrink: true }} />
          <TextField label={intl.formatMessage({ id: 'issued_at' })} type="text" placeholder="YYYY-MM-DD or YYYY-MM or YYYY" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label={intl.formatMessage({ id: 'expires_at' })} type="text" placeholder="YYYY-MM-DD or YYYY-MM or YYYY" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <div>
            <RulesHintBox rules={activeRules} lang={lang} />
            <TextField label={intl.formatMessage({ id: 'DocumentIdentifier' })} value={form.remarks} onChange={(e) => handleRemarksChange(e.target.value)} variant="outlined" size="small" fullWidth style={{ marginTop: activeRules ? 8 : 0 }} placeholder={generateExample(activeRules) ? `e.g. ${generateExample(activeRules)}` : intl.formatMessage({ id: 'EnterDocumentIdentifier' })} InputLabelProps={{ shrink: true }} error={!!remarksError} helperText={remarksError || ''} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={saving}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button onClick={handleSave} disabled={saving || !!remarksError || (!editId && (!form.document_type_id || !form.internal_id))} variant="contained" style={{ backgroundColor: '#363740', color: '#fff' }}>
            {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : intl.formatMessage({ id: 'save' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>{intl.formatMessage({ id: 'delete' })} {intl.formatMessage({ id: 'Eligibility' })}</DialogTitle>
        <DialogContent><p style={{ margin: 0 }}>{intl.formatMessage({ id: 'AreYouSureDelete' })}</p></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button onClick={() => handleDelete(deleteConfirmId)} variant="contained" style={{ backgroundColor: '#e53935', color: '#fff' }}>{intl.formatMessage({ id: 'delete' })}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserEligibility;
